import React, { useState, useRef } from 'react';
import API from '../api';
import { X, Upload, Sparkles, Trash2, Plus, ChevronUp, ChevronDown, AlertTriangle, Check, Loader2 } from 'lucide-react';

/**
 * Modale di import AI per Computi Metrici e Preventivi.
 *
 * Props:
 *  - tipo: 'computo' | 'preventivo'
 *  - onClose(): chiude il modale
 *  - onImport(voci, meta): callback con voci confermate ed eventuali metadati.
 *      L'host page si occupa di creare l'entità (computo o preventivo) e
 *      inserire le righe usando i propri endpoint.
 */
export default function ImportAIModal({ tipo = 'computo', onClose, onImport }) {
  const [step, setStep] = useState('upload'); // upload | parsing | preview
  const [file, setFile] = useState(null);
  const [voci, setVoci] = useState([]);
  const [meta, setMeta] = useState(null);
  const [sampleId, setSampleId] = useState(null);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef(null);

  const tipoLabel = tipo === 'preventivo' ? 'Preventivo' : 'Computo Metrico';

  const handleFile = (f) => {
    if (!f) return;
    const name = f.name.toLowerCase();
    const ok = ['.docx', '.pdf', '.xlsx', '.xlsm', '.png', '.jpg', '.jpeg', '.webp', '.txt']
      .some(ext => name.endsWith(ext));
    if (!ok) {
      setError('Formato non supportato. Usa: DOCX, PDF, XLSX, PNG, JPG.');
      return;
    }
    if (f.size > 15 * 1024 * 1024) {
      setError('File troppo grande (max 15 MB).');
      return;
    }
    setFile(f);
    setError('');
  };

  const startParsing = async () => {
    if (!file) return;
    setStep('parsing');
    setError('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('tipo', tipo);
      // Avvio job asincrono (evita timeout ingress 60s su computi lunghi)
      const { data: job } = await API.post('/api/import/parse', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60000,
      });
      const jobId = job.job_id;
      if (!jobId) throw new Error('Job non avviato');

      // Polling fino a done/error (max ~5 minuti)
      const started = Date.now();
      let result = null;
      while (Date.now() - started < 300000) {
        await new Promise(r => setTimeout(r, 2500));
        const { data: st } = await API.get(`/api/ai/jobs/${jobId}`, { timeout: 30000 });
        if (st.status === 'done') { result = st.result; break; }
        if (st.status === 'error') throw new Error(st.error || 'Errore analisi AI');
      }
      if (!result) throw new Error('Analisi troppo lunga: riprova con un file più piccolo.');

      setVoci(result.voci || []);
      setMeta(result.meta || null);
      setSampleId(result.sample_id || null);
      if (!result.voci || result.voci.length === 0) {
        setError("L'AI non è riuscita a estrarre voci dal documento.");
      }
      setStep('preview');
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message || 'Errore sconosciuto';
      setError(`Errore durante l'analisi: ${msg}`);
      setStep('upload');
    }
  };

  const updateVoce = (idx, field, value) => {
    setVoci(prev => prev.map((v, i) => {
      if (i !== idx) return v;
      const updated = { ...v, [field]: value };
      // Auto-ricalcolo importo se modifico qta o prezzo unitario
      if (field === 'quantita' || field === 'prezzo_unitario') {
        const q = parseFloat(field === 'quantita' ? value : v.quantita) || 0;
        const p = parseFloat(field === 'prezzo_unitario' ? value : v.prezzo_unitario) || 0;
        updated.importo = q && p ? Number((q * p).toFixed(2)) : v.importo;
      }
      return updated;
    }));
  };

  const removeVoce = (idx) => {
    setVoci(prev => prev.filter((_, i) => i !== idx));
  };

  const mergeWithNext = (idx) => {
    setVoci(prev => {
      if (idx >= prev.length - 1) return prev;
      const merged = { ...prev[idx] };
      const next = prev[idx + 1];
      merged.descrizione = `${merged.descrizione} ${next.descrizione}`.trim();
      if (!merged.codice && next.codice) merged.codice = next.codice;
      if (!merged.categoria && next.categoria) merged.categoria = next.categoria;
      if (next.formula) merged.formula = [merged.formula, next.formula].filter(Boolean).join('; ');
      // Se la riga corrente non ha prezzo, eredita dalla prossima (caso classico)
      if (!merged.importo && next.importo) merged.importo = next.importo;
      if (!merged.quantita && next.quantita) merged.quantita = next.quantita;
      if (!merged.prezzo_unitario && next.prezzo_unitario) merged.prezzo_unitario = next.prezzo_unitario;
      if (!merged.unita_misura && next.unita_misura) merged.unita_misura = next.unita_misura;
      if (next.tipo === 'corpo') merged.tipo = 'corpo';
      merged.confidenza = Math.min(merged.confidenza ?? 1, next.confidenza ?? 1);
      return [...prev.slice(0, idx), merged, ...prev.slice(idx + 2)];
    });
  };

  const addEmpty = () => {
    setVoci(prev => [...prev, {
      id: `new-${Date.now()}`,
      codice: '',
      categoria: '',
      descrizione: '',
      formula: '',
      unita_misura: '',
      quantita: null,
      prezzo_unitario: null,
      importo: null,
      tipo: 'normale',
      confidenza: 1.0,
    }]);
  };

  const confirmImport = async () => {
    if (voci.length === 0) {
      setError('Nessuna voce da importare.');
      return;
    }
    const invalid = voci.filter(v => !v.descrizione || !v.descrizione.trim());
    if (invalid.length > 0) {
      setError(`${invalid.length} voce/i senza descrizione. Compilale o eliminale.`);
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      await onImport(voci, meta, sampleId);
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message || 'Errore';
      setError(`Errore in fase di import: ${msg}`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[92vh] flex flex-col" data-testid="import-ai-modal">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center gap-2">
            <Sparkles size={20} className="text-primary" />
            <h2 className="text-lg font-bold">Importa {tipoLabel} con AI</h2>
          </div>
          <button onClick={onClose} aria-label="Chiudi" data-testid="import-ai-close">
            <X size={20} className="text-slate-400 hover:text-slate-600" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-auto p-5">
          {/* STEP UPLOAD */}
          {step === 'upload' && (
            <div className="max-w-2xl mx-auto">
              <p className="text-sm text-slate-600 mb-4">
                Carica un documento (DOCX, PDF, XLSX, immagine). L'AI analizza il contenuto
                e propone le voci di prezzo strutturate per la tua revisione prima dell'import.
              </p>
              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                accept=".docx,.pdf,.xlsx,.xlsm,.png,.jpg,.jpeg,.webp,.txt"
                onChange={(e) => handleFile(e.target.files?.[0])}
                data-testid="import-ai-file-input"
              />
              <div
                onClick={() => fileInputRef.current?.click()}
                onDragOver={(e) => { e.preventDefault(); }}
                onDrop={(e) => { e.preventDefault(); handleFile(e.dataTransfer.files?.[0]); }}
                className="border-2 border-dashed border-slate-300 rounded-lg p-12 text-center cursor-pointer hover:border-primary hover:bg-primary transition-colors"
                data-testid="import-ai-dropzone"
              >
                <Upload size={40} className="mx-auto text-slate-400 mb-3" />
                {file ? (
                  <div>
                    <p className="font-bold text-slate-800">{file.name}</p>
                    <p className="text-xs text-slate-500 mt-1">
                      {(file.size / 1024).toFixed(1)} KB · Clicca per cambiare file
                    </p>
                  </div>
                ) : (
                  <div>
                    <p className="font-bold text-slate-700">Clicca o trascina un file qui</p>
                    <p className="text-xs text-slate-500 mt-1">DOCX · PDF · XLSX · PNG/JPG · max 15 MB</p>
                  </div>
                )}
              </div>
              {error && (
                <div className="mt-3 p-3 bg-red-50 text-red-700 text-sm rounded flex items-start gap-2">
                  <AlertTriangle size={16} className="flex-shrink-0 mt-0.5" />
                  <span>{error}</span>
                </div>
              )}
              <div className="mt-4 p-3 bg-amber-50 border border-amber-200 text-amber-800 text-xs rounded">
                💡 L'AI raggruppa righe della stessa voce (descrizione multi-paragrafo + prezzo finale).
                Avrai sempre l'anteprima editabile prima dell'import definitivo.
              </div>
              <div className="mt-5 flex justify-end gap-2">
                <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded" data-testid="import-ai-cancel">
                  Annulla
                </button>
                <button
                  onClick={startParsing}
                  disabled={!file}
                  className="px-4 py-2 bg-primary text-white font-bold rounded hover:bg-primary-hover disabled:opacity-50 flex items-center gap-2"
                  data-testid="import-ai-analyze-btn"
                >
                  <Sparkles size={16} /> Analizza con AI
                </button>
              </div>
            </div>
          )}

          {/* STEP PARSING */}
          {step === 'parsing' && (
            <div className="flex flex-col items-center justify-center py-20">
              <Loader2 size={48} className="text-primary animate-spin mb-4" />
              <p className="font-bold text-slate-700">Geppo sta leggendo il documento…</p>
              <p className="text-sm text-slate-500 mt-1">Può richiedere 20-60 secondi a seconda della lunghezza.</p>
            </div>
          )}

          {/* STEP PREVIEW */}
          {step === 'preview' && (
            <div>
              <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <div>
                  <p className="text-sm text-slate-600">
                    Voci rilevate: <span className="font-bold text-slate-900">{voci.length}</span>
                    {meta?.filename && <> · <span className="font-mono text-xs">{meta.filename}</span></>}
                  </p>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Rivedi, correggi, unisci righe spezzate o aggiungi voci mancanti.
                  </p>
                </div>
                <button onClick={addEmpty} className="text-sm px-3 py-1.5 border border-slate-300 rounded hover:bg-slate-50 flex items-center gap-1" data-testid="import-ai-add-row">
                  <Plus size={14} /> Aggiungi voce
                </button>
              </div>

              {error && (
                <div className="mb-3 p-2 bg-red-50 text-red-700 text-sm rounded flex items-center gap-2">
                  <AlertTriangle size={14} /> {error}
                </div>
              )}

              <div className="space-y-2 max-h-[55vh] overflow-y-auto pr-1">
                {voci.map((v, idx) => {
                  const lowConf = (v.confidenza ?? 1) < 0.7;
                  return (
                    <div key={v.id || idx}
                      className={`border rounded p-3 ${lowConf ? 'border-amber-300 bg-amber-50/40' : 'border-slate-200'}`}
                      data-testid={`import-ai-row-${idx}`}
                    >
                      <div className="flex items-start gap-2">
                        <span className="text-xs font-mono text-slate-400 mt-2 w-6">{idx + 1}</span>
                        <div className="flex-1 grid grid-cols-12 gap-2">
                          <input
                            value={v.codice || ''}
                            onChange={(e) => updateVoce(idx, 'codice', e.target.value)}
                            placeholder="Codice / Tariffa"
                            className="col-span-3 px-2 py-1.5 border border-slate-200 rounded text-sm font-mono"
                            data-testid={`import-ai-codice-${idx}`}
                          />
                          <input
                            value={v.categoria || ''}
                            onChange={(e) => updateVoce(idx, 'categoria', e.target.value)}
                            placeholder="Categoria / Capitolo (SpCat)"
                            className="col-span-9 px-2 py-1.5 border border-slate-200 rounded text-sm"
                            data-testid={`import-ai-categoria-${idx}`}
                          />
                          <textarea
                            value={v.descrizione || ''}
                            onChange={(e) => updateVoce(idx, 'descrizione', e.target.value)}
                            placeholder="Descrizione voce…"
                            rows={2}
                            className="col-span-12 px-2 py-1.5 border border-slate-200 rounded text-sm resize-y"
                          />
                          <input
                            value={v.formula || ''}
                            onChange={(e) => updateVoce(idx, 'formula', e.target.value)}
                            placeholder="Formula dimensioni (es. 1,35+2,05=3,40)"
                            className="col-span-12 px-2 py-1.5 border border-blue-200 bg-blue-50/40 rounded text-xs font-mono"
                            data-testid={`import-ai-formula-${idx}`}
                          />
                          <input
                            value={v.unita_misura || ''}
                            onChange={(e) => updateVoce(idx, 'unita_misura', e.target.value)}
                            placeholder="U.M."
                            className="col-span-2 px-2 py-1.5 border border-slate-200 rounded text-sm"
                          />
                          <input
                            type="number"
                            step="0.01"
                            value={v.quantita ?? ''}
                            onChange={(e) => updateVoce(idx, 'quantita', e.target.value === '' ? null : parseFloat(e.target.value))}
                            placeholder="Qtà"
                            className="col-span-2 px-2 py-1.5 border border-slate-200 rounded text-sm"
                          />
                          <input
                            type="number"
                            step="0.01"
                            value={v.prezzo_unitario ?? ''}
                            onChange={(e) => updateVoce(idx, 'prezzo_unitario', e.target.value === '' ? null : parseFloat(e.target.value))}
                            placeholder="Prezzo U."
                            className="col-span-2 px-2 py-1.5 border border-slate-200 rounded text-sm"
                          />
                          <input
                            type="number"
                            step="0.01"
                            value={v.importo ?? ''}
                            onChange={(e) => updateVoce(idx, 'importo', e.target.value === '' ? null : parseFloat(e.target.value))}
                            placeholder="Importo"
                            className="col-span-2 px-2 py-1.5 border border-slate-200 rounded text-sm font-bold"
                          />
                          <select
                            value={v.tipo || 'normale'}
                            onChange={(e) => updateVoce(idx, 'tipo', e.target.value)}
                            className="col-span-2 px-2 py-1.5 border border-slate-200 rounded text-sm"
                          >
                            <option value="normale">Normale</option>
                            <option value="corpo">A corpo</option>
                          </select>
                          <div className="col-span-2 flex items-center gap-1">
                            {lowConf && <span title="Bassa confidenza AI" className="text-amber-600 text-xs">⚠ bassa conf.</span>}
                          </div>
                        </div>
                        <div className="flex flex-col gap-1 ml-1">
                          <button
                            onClick={() => mergeWithNext(idx)}
                            disabled={idx >= voci.length - 1}
                            title="Unisci con la riga sotto"
                            className="p-1 text-slate-500 hover:text-blue-600 disabled:opacity-30"
                            data-testid={`import-ai-merge-${idx}`}
                          >
                            <ChevronDown size={16} />
                          </button>
                          <button
                            onClick={() => removeVoce(idx)}
                            title="Elimina"
                            className="p-1 text-slate-500 hover:text-red-600"
                            data-testid={`import-ai-delete-${idx}`}
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="mt-4 pt-3 border-t flex items-center justify-between flex-wrap gap-2">
                <div className="text-sm text-slate-600">
                  Totale stimato: <span className="font-bold text-slate-900">
                    € {voci.reduce((s, v) => s + (Number(v.importo) || ((Number(v.quantita) || 0) * (Number(v.prezzo_unitario) || 0))), 0).toFixed(2)}
                  </span>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => setStep('upload')} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded">
                    ← Cambia file
                  </button>
                  <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded">
                    Annulla
                  </button>
                  <button
                    onClick={confirmImport}
                    disabled={submitting || voci.length === 0}
                    className="px-5 py-2 bg-primary text-white font-bold rounded hover:bg-primary-hover disabled:opacity-50 flex items-center gap-2"
                    data-testid="import-ai-confirm-btn"
                  >
                    {submitting ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
                    Conferma e importa
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
